/** Tela 20 — Administração › Perfis e permissões por ação (DEC-12). */
import { useCallback, useEffect, useState } from 'react';
import { api, ErroApi } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { Aviso, Campo, ConfirmarAcao, Modal, Selo, SeloStatus } from '../componentes/basicos.tsx';
import { Ajuda } from '../comercial/ui.tsx';
import { rotuloEspecifica } from './rotulosPermissao.ts';

interface Perfil {
  id: string;
  nome: string;
  status: 'ativo' | 'inativo';
  usuarios_ativos: number;
  perfil_base: boolean;
  atualizado_em: string;
}
interface ItemMatriz {
  entidade_alvo: string;
  acao: string;
  permitido: boolean;
}

const ACOES = ['visualizar', 'criar', 'editar', 'inativar', 'exportar'];
const ROTULO_ACAO: Record<string, string> = { visualizar: 'Visualizar', criar: 'Criar', editar: 'Editar', inativar: 'Inativar', exportar: 'Exportar' };
/** P2-05: entidades cujas ações ficam todas na seção "Ações específicas" (não se repetem na tabela-padrão). */
const ENTIDADES_DOCUMENTO = ['documento_cliente', 'documento_oportunidade', 'documento_financeiro'];
const AJUDA_ESPECIFICA: Record<string, string> = {
  excluir: 'Envia à Lixeira com justificativa, passo a passo (vínculos ativos impedem). Nada é apagado.',
  ver_lixeira: 'Consulta os registros desta entidade em Administração › Lixeira.',
  restaurar: 'Devolve o registro às listas com os mesmos dados. Exige também "Ver na Lixeira".',
  enviar: 'Novo documento ou nova versão (a anterior fica no histórico).',
  inativar: 'Retira o documento com justificativa, auditada; nada é apagado.',
  ver_restrito: 'Vê o nome e baixa o arquivo de documentos marcados como restritos (ex.: dossiê da Locação).',
  reabrir_correcao: 'Corrige valores, quantidades, parceiro e unidade da cadeia, com prévia, justificativa e auditoria; preserva versões, NFs e pagamentos.',
  reabrir_comercial: 'Retoma a negociação e revoga o resultado (só sem faturamento ou pagamento válido).',
  acompanhar_locacao: 'Registra etapas, dossiê, parcelas, pagamentos do Cliente e recebimentos da Inttrix na OL.',
  confirmar_documento: 'Completa e confirma o documento (fatura/guia) de uma despesa, inclusive depois do pagamento.',
  estornar: 'Estorna um pagamento com justificativa; o original fica preservado.',
  desfazer: 'Desfaz um vínculo de conciliação com justificativa (necessário antes de estornar um pagamento conciliado).',
  reabrir: 'Reabre um período financeiro fechado, com justificativa na auditoria.',
  cancelar_comissao: 'Cancela a comissão com justificativa; parcelas, pagamentos e vínculos ficam preservados.',
  cancelar_nota: 'Cancela a nota (com tratamento do valor já recebido: crédito ou devolução) e libera os itens para nova cobrança; nada é apagado.',
  estornar_recebimento: 'Estorna um recebimento registrado por engano, com justificativa; o original fica preservado.',
  gerir_credito: 'Aplica, devolve ou desfaz o crédito do pagador, com justificativa.',
  completar_legado: 'Completa em lote, por parceiro, campos ausentes das operações antigas (com prévia, seleção explícita e histórico).',
  solicitar_resgate: 'Registra (ou cancela, com justificativa) a solicitação de resgate feita ao parceiro, por item ou grupo.',
};
const ehEspecifica = (ent: string, acao: string) => ENTIDADES_DOCUMENTO.includes(ent) || !ACOES.includes(acao);

export function TelaPerfis() {
  const { pode } = useSessao();
  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [matriz, setMatriz] = useState<ItemMatriz[]>([]);
  const [original, setOriginal] = useState<ItemMatriz[]>([]);
  const [editavel, setEditavel] = useState(false);
  const [rotulos, setRotulos] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [dialogo, setDialogo] = useState<{ tipo: 'novo' } | { tipo: 'renomear'; p: Perfil } | { tipo: 'status'; p: Perfil } | { tipo: 'salvar' } | null>(null);

  const carregar = useCallback(async () => {
    const lista = await api.get<Perfil[]>('/api/perfis');
    setPerfis(lista);
    setSelecionado((s) => s ?? lista[0]?.id ?? null);
  }, []);

  useEffect(() => {
    carregar().catch((e) => setErro(e.message));
    api.get('/api/permissoes/catalogo').then((c) => setRotulos(c.rotulos)).catch(() => undefined);
  }, [carregar]);

  useEffect(() => {
    if (!selecionado || !pode('visualizar', 'permissao')) return;
    api
      .get(`/api/perfis/${selecionado}/permissoes`)
      .then((r) => {
        setMatriz(r.permissoes);
        setOriginal(r.permissoes);
        setEditavel(r.editavel && pode('editar', 'permissao'));
      })
      .catch((e) => setErro(e.message));
  }, [selecionado, pode]);

  async function executar(acao: () => Promise<unknown>, mensagem: string): Promise<boolean> {
    setErro(null);
    setSucesso(null);
    try {
      await acao();
      setDialogo(null);
      setSucesso(mensagem);
      await carregar();
      return true;
    } catch (e) {
      setErro((e as ErroApi).message);
      return false;
    }
  }

  const entidades = [...new Set(matriz.map((m) => m.entidade_alvo))].filter((ent) => !ENTIDADES_DOCUMENTO.includes(ent));
  const alteracoes = matriz.filter((m) => original.find((o) => o.acao === m.acao && o.entidade_alvo === m.entidade_alvo)?.permitido !== m.permitido);
  const perfilAtual = perfis.find((p) => p.id === selecionado);

  return (
    <section className="duas-colunas">
      <div className="coluna-lista">
        <div className="barra-ferramentas">
          <h3>Perfis</h3>
          <span className="espaco" />
          {pode('criar', 'perfil') && (
            <button className="botao-primario pequeno" onClick={() => setDialogo({ tipo: 'novo' })}>
              Novo perfil
            </button>
          )}
        </div>
        <ul className="lista-selecao">
          {perfis.map((p) => (
            <li key={p.id}>
              <button className={p.id === selecionado ? 'selecionado' : ''} onClick={() => setSelecionado(p.id)}>
                <span>{p.nome}</span>
                <small>
                  {p.usuarios_ativos} usuário(s) {p.status === 'inativo' && <Selo tom="inativo">Inativo</Selo>}
                </small>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="coluna-detalhe">
        {!dialogo && <Aviso>{erro}</Aviso>}
        <Aviso tipo="sucesso">{sucesso}</Aviso>
        {perfilAtual && (
          <>
            <div className="barra-ferramentas">
              <h3>
                {perfilAtual.nome} <SeloStatus ativo={perfilAtual.status === 'ativo'} />
              </h3>
              <span className="espaco" />
              {pode('editar', 'perfil') && !perfilAtual.perfil_base && (
                <button className="botao-link" onClick={() => setDialogo({ tipo: 'renomear', p: perfilAtual })}>
                  Renomear
                </button>
              )}
              {pode('inativar', 'perfil') && perfilAtual.nome !== 'Administrador' && (
                <button className="botao-link" onClick={() => setDialogo({ tipo: 'status', p: perfilAtual })}>
                  {perfilAtual.status === 'ativo' ? 'Inativar' : 'Reativar'}
                </button>
              )}
            </div>
            {perfilAtual.nome === 'Administrador' && (
              <Aviso tipo="info">O perfil Administrador tem acesso total e suas permissões são fixas (proteção contra perda de acesso administrativo).</Aviso>
            )}
            {pode('visualizar', 'permissao') && (
              <>
                <div className="rolagem quadro-tabela">
                <table className="tabela matriz">
                  <thead>
                    <tr>
                      <th>Entidade</th>
                      {ACOES.map((a) => (
                        <th key={a}>{ROTULO_ACAO[a]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entidades.map((ent) => (
                      <tr key={ent}>
                        <td>{rotulos[ent] ?? ent}</td>
                        {ACOES.map((a) => {
                          const item = matriz.find((m) => m.entidade_alvo === ent && m.acao === a);
                          if (!item) return <td key={a} className="nao-aplica">—</td>;
                          return (
                            <td key={a}>
                              <input
                                type="checkbox"
                                aria-label={`${ROTULO_ACAO[a]} ${rotulos[ent] ?? ent}`}
                                checked={item.permitido}
                                disabled={!editavel}
                                onChange={(e) =>
                                  setMatriz((mm) => mm.map((m) => (m === item ? { ...m, permitido: e.target.checked } : m)))
                                }
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {editavel && (
                  <div className="acoes-form">
                    <button className="botao-secundario" disabled={!alteracoes.length} onClick={() => setMatriz(original)}>
                      Descartar
                    </button>
                    <button className="botao-primario" disabled={!alteracoes.length} onClick={() => setDialogo({ tipo: 'salvar' })}>
                      Salvar permissões ({alteracoes.length})
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
        {pode('visualizar', 'permissao') && <AcoesEspecificas perfis={perfis} rotulos={rotulos} editavel={pode('editar', 'permissao')} aoSalvar={(m) => (setSucesso(m), void carregar())} />}
      </div>

      {(dialogo?.tipo === 'novo' || dialogo?.tipo === 'renomear') && (
        <Modal titulo={dialogo.tipo === 'novo' ? 'Novo perfil' : 'Renomear perfil'} aoFechar={() => setDialogo(null)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const nome = new FormData(e.currentTarget).get('nome');
              if (dialogo.tipo === 'novo') executar(() => api.post('/api/perfis', { nome }), 'Perfil criado — sem permissões até que sejam concedidas.');
              else executar(() => api.patch(`/api/perfis/${dialogo.p.id}`, { nome, atualizado_em: dialogo.p.atualizado_em }), 'Perfil renomeado.');
            }}
          >
            <Aviso>{erro}</Aviso>
            <Campo rotulo="Nome do perfil">{(id) => <input id={id} name="nome" required maxLength={100} defaultValue={dialogo.tipo === 'renomear' ? dialogo.p.nome : ''} />}</Campo>
            <div className="acoes-form">
              <button type="button" className="botao-secundario" onClick={() => setDialogo(null)}>
                Cancelar
              </button>
              <button className="botao-primario" type="submit">
                Salvar
              </button>
            </div>
          </form>
        </Modal>
      )}

      {dialogo?.tipo === 'status' && (
        <ConfirmarAcao
          erro={erro}
          titulo={dialogo.p.status === 'ativo' ? 'Inativar perfil' : 'Reativar perfil'}
          descricao={<>Confirmar a {dialogo.p.status === 'ativo' ? 'inativação' : 'reativação'} do perfil <strong>{dialogo.p.nome}</strong>?</>}
          rotuloConfirmar={dialogo.p.status === 'ativo' ? 'Inativar' : 'Reativar'}
          aoFechar={() => setDialogo(null)}
          aoConfirmar={(j) =>
            executar(() => api.post(`/api/perfis/${dialogo.p.id}/${dialogo.p.status === 'ativo' ? 'inativar' : 'reativar'}`, { justificativa: j || undefined }), 'Situação do perfil atualizada.')
          }
        />
      )}

      {dialogo?.tipo === 'salvar' && perfilAtual && (
        <ConfirmarAcao
          erro={erro}
          titulo="Salvar permissões"
          descricao={
            <>
              {alteracoes.length} alteração(ões) no perfil <strong>{perfilAtual.nome}</strong>. Elas valem a partir da próxima ação dos usuários desse perfil.
            </>
          }
          rotuloConfirmar="Salvar"
          aoFechar={() => setDialogo(null)}
          aoConfirmar={async (j) => {
            const ok = await executar(() => api.put(`/api/perfis/${perfilAtual.id}/permissoes`, { permissoes: alteracoes, justificativa: j || undefined }), 'Permissões salvas.');
            if (ok) setOriginal(matriz);
          }}
        />
      )}
    </section>
  );
}

/**
 * P2-05 (D6): ações que não cabem em Visualizar/Criar/Editar/Inativar/Exportar — documentos (ver, enviar, inativar),
 * OC do Cliente, faturamento, PTAX… — agora visíveis e concedíveis pela tela, uma coluna por perfil
 * (Administrador fica fora: acesso total e fixo). Salvar grava por perfil, com justificativa e auditoria.
 */
function AcoesEspecificas({ perfis, rotulos, editavel, aoSalvar }: { perfis: Perfil[]; rotulos: Record<string, string>; editavel: boolean; aoSalvar: (mensagem: string) => void }) {
  const colunas = perfis.filter((p) => p.nome !== 'Administrador' && p.status === 'ativo');
  const ids = colunas.map((p) => p.id).join(',');
  const [dados, setDados] = useState<Record<string, ItemMatriz[]>>({});
  const [original, setOriginal] = useState<Record<string, ItemMatriz[]>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState(false);
  useEffect(() => {
    if (!ids) return;
    Promise.all(ids.split(',').map((id) => api.get(`/api/perfis/${id}/permissoes`).then((r) => [id, (r.permissoes as ItemMatriz[]).filter((m) => ehEspecifica(m.entidade_alvo, m.acao))] as const)))
      .then((l) => {
        const m = Object.fromEntries(l);
        setDados(m);
        setOriginal(m);
      })
      .catch((e) => setErro(e.message));
  }, [ids]);
  const primeiro = dados[colunas[0]?.id] ?? [];
  if (!colunas.length || !primeiro.length) return erro ? <Aviso>{erro}</Aviso> : null;
  const linhas = primeiro.map((m) => ({ entidade: m.entidade_alvo, acao: m.acao }));
  const grupos = [...new Set(linhas.map((l) => l.entidade))].sort((a, b) => Number(!ENTIDADES_DOCUMENTO.includes(a)) - Number(!ENTIDADES_DOCUMENTO.includes(b)));
  const valor = (perfil: string, ent: string, acao: string) => dados[perfil]?.find((m) => m.entidade_alvo === ent && m.acao === acao)?.permitido ?? false;
  const alteracoes = colunas.flatMap((p) => (dados[p.id] ?? []).filter((m) => (original[p.id] ?? []).find((o) => o.acao === m.acao && o.entidade_alvo === m.entidade_alvo)?.permitido !== m.permitido).map((m) => ({ perfil: p, m })));
  const marcar = (perfil: string, ent: string, acao: string, v: boolean) => setDados((d) => ({ ...d, [perfil]: d[perfil].map((m) => (m.entidade_alvo === ent && m.acao === acao ? { ...m, permitido: v } : m)) }));
  return (
    <section className="acoes-especificas" data-teste="acoes-especificas">
      <h3>Ações específicas</h3>
      <p className="texto-secundario sem-margem">Ações que não cabem em Visualizar/Criar/Editar/Inativar/Exportar, uma coluna por perfil. O Administrador tem todas, fixas.</p>
      <Aviso>{erro}</Aviso>
      <div className="rolagem quadro-tabela">
        <table className="tabela tabela-compacta matriz-especificas">
          <thead>
            <tr>
              <th>Entidade</th>
              <th>Ação</th>
              {colunas.map((p) => (
                <th key={p.id} className="centro">
                  {p.nome}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grupos.map((ent) => {
              const acoes = linhas.filter((l) => l.entidade === ent);
              return acoes.map((l, i) => (
                <tr key={`${ent}:${l.acao}`} className={i === 0 ? 'inicio-grupo' : undefined} data-teste={`especifica-${ent}-${l.acao}`}>
                  {i === 0 && (
                    <th scope="rowgroup" rowSpan={acoes.length} className="entidade-grupo">
                      {rotulos[ent] ?? ent}
                    </th>
                  )}
                  <td className="nowrap">
                    {rotuloEspecifica(ent, l.acao)}
                    {ENTIDADES_DOCUMENTO.includes(ent) && AJUDA_ESPECIFICA[l.acao] && (
                      <>
                        
                        <Ajuda titulo={`${rotuloEspecifica(ent, l.acao)} · ${rotulos[ent] ?? ent}`}>{AJUDA_ESPECIFICA[l.acao]}</Ajuda>
                      </>
                    )}
                  </td>
                  {colunas.map((p) => (
                    <td key={p.id} className="centro">
                      <input type="checkbox" aria-label={`${rotuloEspecifica(ent, l.acao)} ${rotulos[ent] ?? ent} — ${p.nome}`} checked={valor(p.id, ent, l.acao)} disabled={!editavel} onChange={(e) => marcar(p.id, ent, l.acao, e.target.checked)} data-teste={`especifica-${ent}-${l.acao}-${p.nome}`} />
                    </td>
                  ))}
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
      {editavel && (
        <div className="acoes-form">
          <button className="botao-secundario" disabled={!alteracoes.length} onClick={() => setDados(original)}>
            Descartar
          </button>
          <button className="botao-primario" disabled={!alteracoes.length} onClick={() => setConfirmar(true)} data-teste="salvar-acoes-especificas">
            Salvar ações específicas ({alteracoes.length})
          </button>
        </div>
      )}
      {confirmar && (
        <ConfirmarAcao
          erro={erro}
          titulo="Salvar ações específicas"
          descricao={
            <>
              {alteracoes.length} alteração(ões) em {[...new Set(alteracoes.map((a) => a.perfil.nome))].join(', ')}. Elas valem a partir da próxima ação dos usuários desses perfis.
            </>
          }
          rotuloConfirmar="Salvar"
          aoFechar={() => setConfirmar(false)}
          aoConfirmar={async (j) => {
            setErro(null);
            try {
              for (const p of colunas) {
                const lista = alteracoes.filter((a) => a.perfil.id === p.id).map((a) => a.m);
                if (lista.length) await api.put(`/api/perfis/${p.id}/permissoes`, { permissoes: lista, justificativa: j || undefined });
              }
              setOriginal(dados);
              setConfirmar(false);
              aoSalvar('Ações específicas salvas.');
            } catch (e) {
              setErro((e as ErroApi).message);
            }
          }}
        />
      )}
    </section>
  );
}
